import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Input, Dropdown, Form, Flag } from 'semantic-ui-react';
import { selectUserCountry } from '../../redux/slices/locationSlice';
import { getCountries, getCountryCallingCode, parsePhoneNumber, AsYouType, isSupportedCountry } from 'libphonenumber-js';

/**
 * Phone input component with country code selection using Semantic UI
 */
const PhoneInput = ({ 
  label, 
  value = '', 
  onChange,
  showValidation = true, 
  validationMessage,
  userLang,
  onValidationChange,
  className = '',
  required = false,
  selectedCountryIso = '',
  disabled = false,
  disableCountrySelection = false,
  error,
  compact = false,
  ...props 
}) => {
  // Get user's country from Redux state
  const userCountry = useSelector(selectUserCountry);
  
  const [countryCode, setCountryCode] = useState('');
  const [selectedCountryIsoState, setSelectedCountryIsoState] = useState(selectedCountryIso || '');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [validationError, setValidationError] = useState('');
  const [touched, setTouched] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Create a countries list with unique identifiers even for shared calling codes
  const countries = getCountries().map(country => {
    const code = `+${getCountryCallingCode(country)}`;
    return {
      key: country.toLowerCase(),
      value: country,
      text: `${code} ${new Intl.DisplayNames([userLang === 'zh-CN' ? 'zh' : 'en'], { type: 'region' }).of(country)}`,
      content: (
        <div className="flex items-center">
          <Flag name={country.toLowerCase()} />
          <span className="ml-2">{code}</span>
          <span className="ml-2">{new Intl.DisplayNames([userLang === 'zh-CN' ? 'zh' : 'en'], { type: 'region' }).of(country)}</span>
        </div>
      ),
      code: code, // The calling code like +1
      country: country, // The ISO code like 'US'
    };
  }).sort((a, b) => {
    // Special handling for common countries to appear at the top
    const topCountries = ['US', 'CN', 'GB', 'AU', 'CA'];
    const aIndex = topCountries.indexOf(a.country);
    const bIndex = topCountries.indexOf(b.country);
    
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    
    return a.text.localeCompare(b.text);
  });
  
  // Get country from browser info as fallback
  const getCountryFromBrowser = () => {
    try {
      // Try to get from language
      const browserLanguage = navigator.language || navigator.userLanguage || 'en-US';
      
      if (browserLanguage.includes('-')) {
        const country = browserLanguage.split('-')[1];
        if (isSupportedCountry(country)) {
          return country;
        }
      }
      
      // Try to get from timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      if (timezone) {
        // Some common timezones to countries mapping
        const timezoneMap = {
          'America/New_York': 'US',
          'America/Los_Angeles': 'US',
          'America/Chicago': 'US',
          'Europe/London': 'GB',
          'Europe/Paris': 'FR',
          'Europe/Berlin': 'DE',
          'Asia/Shanghai': 'CN',
          'Asia/Tokyo': 'JP',
          'Australia/Sydney': 'AU'
        };
        
        if (timezoneMap[timezone]) {
          return timezoneMap[timezone];
        }
      }
      
      return 'US';
    } catch (e) {
      console.warn('Error detecting country from browser', e);
      return 'US';
    }
  };
  
  // Update when selectedCountryIso prop changes
  useEffect(() => {
    if (selectedCountryIso && selectedCountryIso !== selectedCountryIsoState) {
      setSelectedCountryIsoState(selectedCountryIso);
      
      // Set the country code based on the ISO code
      try {
        const callingCode = `+${getCountryCallingCode(selectedCountryIso)}`;
        setCountryCode(callingCode);
      } catch (e) {
        console.warn('Could not get calling code for', selectedCountryIso, e);
      }
    }
  }, [selectedCountryIso, selectedCountryIsoState]);
  
  // Set default country code based on user's country
  useEffect(() => {
    if (!countryCode) {
      let detectedCountryIso;
      
      // First priority: Use the prop if provided
      if (selectedCountryIso && isSupportedCountry(selectedCountryIso)) {
        detectedCountryIso = selectedCountryIso;
      }
      // Second priority: Use country from Redux if available
      else if (userCountry?.code && isSupportedCountry(userCountry.code)) {
        detectedCountryIso = userCountry.code;
      } 
      // Last option: Try to detect from browser
      else {
        detectedCountryIso = getCountryFromBrowser();
      }
      
      if (detectedCountryIso) {
        try {
          const callingCode = `+${getCountryCallingCode(detectedCountryIso)}`;
          setCountryCode(callingCode);
          setSelectedCountryIsoState(detectedCountryIso);
        } catch (e) {
          console.warn('Could not get calling code for', detectedCountryIso, e);
          setCountryCode('+1');
          setSelectedCountryIsoState('US');
        }
      } else {
        setCountryCode('+1');
        setSelectedCountryIsoState('US');
      }
    }
  }, [userCountry, countryCode, selectedCountryIso]);
  
  // Initialize from value if provided
  useEffect(() => {
    if (value) {
      let nationalPart = '';
      let countryPart = null;
      try {
        // Try to parse the value as an international phone number
        const parsedNumber = parsePhoneNumber(value, { extract: true });
        if (parsedNumber && parsedNumber.country) {
          // Successfully parsed with country info
          countryPart = { code: `+${parsedNumber.countryCallingCode}`, iso: parsedNumber.country || '' };
          nationalPart = parsedNumber.nationalNumber;
        } else {
          // Parsing might have succeeded but without country, or failed partially.
          // Check if it LOOKS like it starts with a known country code.
          const knownCodeMatch = countries.find(c => value.startsWith(c.code));
          if (knownCodeMatch) {
            countryPart = { code: knownCodeMatch.code, iso: knownCodeMatch.country };
            // Extract the rest as potential national number
            nationalPart = value.substring(knownCodeMatch.code.length);
          } else {
            // Doesn't start with a known code, assume it's all national number
            // Keep existing country code state
            nationalPart = value;
          }
        }
      } catch (e) { // Catch actual parsing errors (like invalid format)
         // Parsing failed entirely. Check if it LOOKS like it starts with a known country code.
         const knownCodeMatch = countries.find(c => value.startsWith(c.code));
         if (knownCodeMatch) {
           countryPart = { code: knownCodeMatch.code, iso: knownCodeMatch.country };
           nationalPart = value.substring(knownCodeMatch.code.length);
         } else {
           // Doesn't look like it starts with a known code, assume it's all national number
           // Keep existing country code state
           nationalPart = value;
         }
      }

      // --- State Updates --- 
      // Only update if parts actually changed to avoid infinite loops

      // Update country code and ISO if detected and different from current state
      if (countryPart) {
        if (countryPart.code !== countryCode) {
          setCountryCode(countryPart.code);
        }
        if (countryPart.iso !== selectedCountryIsoState) {
          setSelectedCountryIsoState(countryPart.iso);
        }
      }

      // Clean the extracted national part (remove non-digits)
      const cleanNationalPart = nationalPart.replace(/[^0-9]/g, '');

      // Update phoneNumber state ONLY if the clean national part is different
      if (cleanNationalPart !== phoneNumber) {
        setPhoneNumber(cleanNationalPart);
      }

    } else {
      // If value prop is empty, clear the national number state if it's not already empty
      if (phoneNumber !== '') {
        setPhoneNumber('');
      }
    }
    // Dependencies: value prop, countries list (for lookups), and the state vars we compare against
  }, [value, countries, countryCode, selectedCountryIsoState, phoneNumber]);
  
  // Phone number validation using libphonenumber-js
  const validatePhone = (phoneValue, country) => {
    if (!phoneValue) return true;
    
    try {
      // If we have a country ISO code, use that directly
      const countryForParsing = selectedCountryIsoState || 
                               (country?.replace('+', '') ? undefined : undefined);
      
      const parsedPhone = countryForParsing 
        ? parsePhoneNumber(phoneValue, countryForParsing)
        : parsePhoneNumber(`${countryCode}${phoneValue}`);
      
      return parsedPhone && parsedPhone.isValid();
    } catch (e) {
      // If parsing fails, use basic regex
      const re = /^[\d\s()+\-]+$/;
      return re.test(phoneValue);
    }
  };
  
  const handlePhoneNumberChange = (e, { value: inputDigits }) => {
    // 1. Store only the digits entered in the input field.
    //    Remove any non-digit characters just in case, except maybe spaces/hyphens if desired.
    const nationalNumber = inputDigits.replace(/[^0-9]/g, ''); // Basic digit extraction
    setPhoneNumber(nationalNumber); // Update state with ONLY the national number digits

    if (!touched) setTouched(true);

    // 2. Construct the full phone number ONLY for the parent onChange prop.
    const fullPhoneNumber = countryCode ? `${countryCode}${nationalNumber}` : nationalNumber;

    // 3. Call the parent's onChange with the combined number.
    onChange({
      target: {
        name: props.name,
        value: fullPhoneNumber
      }
    });

    // 4. Validate using the national number and the selected country context.
    //    (validatePhoneValue might need adjustment later if validation fails)
    if (showValidation) validatePhoneValue(nationalNumber); 
  };
  
  const handleCountryCodeChange = (e, { value: newCountryIso }) => {
    setSelectedCountryIsoState(newCountryIso);
    
    // Find the country that matches this code
    const selectedCountryObj = countries.find(c => c.country === newCountryIso);
    if (selectedCountryObj) {
      setCountryCode(selectedCountryObj.code);
      
      const fullPhoneNumber = `${selectedCountryObj.code}${phoneNumber}`;
    onChange({
      target: {
        name: props.name,
        value: fullPhoneNumber
      }
    });
    
    // Re-validate with new country code
    if (showValidation && phoneNumber) {
        validatePhoneValue(phoneNumber, selectedCountryObj.code);
      }
    }
  };
  
  const validatePhoneValue = (phoneValue, countryCodeOverride) => {
    let error = '';
    const countryToUse = countryCodeOverride || countryCode;
    
    if (!phoneValue && required) {
      error = userLang === 'zh-CN' ? '请输入电话号码' : 'Phone number is required';
    } else if (phoneValue && !validatePhone(phoneValue, countryToUse)) {
      error = userLang === 'zh-CN' ? '请输入有效的电话号码' : 'Please enter a valid phone number';
    } else if (phoneValue && phoneValue.length < 7) {
      error = userLang === 'zh-CN' ? '电话号码位数不足' : 'Invalid number - Not enough digits.';
    }
    
    setValidationError(error);
    
    if (onValidationChange) {
      onValidationChange(error ? [error] : []);
    }
    
    return error;
  };
  
  // Get the flag for the selected country
  const selectedCountryData = countries.find(c => c.country === selectedCountryIsoState);
  
  return (
    <Form.Field >
        <label className="block text-label font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      
      <Input
        action
        actionPosition='left'
        className={`${className} phone-input`}
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            placeholder={props.placeholder || (userLang === 'zh-CN' ? '手机号码' : 'Phone number')}
            disabled={disabled}
        error={!!error || (touched && validationError)}
        size={compact ? 'small' : 'medium'}
      >
        <Dropdown
          button
          basic
          floating
          options={countries}
          value={selectedCountryIsoState}
          onChange={handleCountryCodeChange}
          disabled={disabled || disableCountrySelection}
          search
          selection={disabled||disableCountrySelection}
          compact={disabled||disableCountrySelection}
          text={selectedCountryData ? selectedCountryData.code : '+1'}
          className='country-dropdown'
        />
        <input />
      </Input>
      
      {(touched && validationError) && (
        <div className="mt-1 text-sm text-red-600">{validationError}</div>
      )}
    </Form.Field>
  );
};

export default PhoneInput; 